// ClientSlideOut — the spine of the ClientHub kit (Epic 1 · S1.6).
//
// A client name anywhere opens this glass slide-out OVER the page; closing
// returns you exactly where you were. Same content, same position, same gestures
// from every page.
//
// Calls three RPCs in one Promise.all (00_CONTRACT.md §8 — "one Promise.all per
// page load; no sequential fetches"):
//   fn_client_context  → { client, attention, work, commitments, threads, people,
//                          billing, effort, elsewhere }
//   fn_signals_for     → signal[] (each: { says, tint, kind, ... })
//   fn_timeline        → { subject, events[], available_types[], next_before_seq }
//
// Contract (00_CONTRACT.md §6 / 01_SHELL_CHARLES.md):
//   - Glass: translucent, blurred, distinct tint, floats over the page.
//   - Standing facts at top (quiet, dense, permanent).
//   - Signals as insight surfaces (tinted, contained).
//   - Event stream is the tallest thing, scrolls.
//   - billing/effort null → <NotPermitted>, never zero, never empty.
//   - elsewhere is a link, not a copy.
//   - Full-screen sheet <768px; right drawer on tablet+.
//   - Esc closes; scrim click closes; focus returns to the trigger.
//
// RPC names/shapes are UNCONFIRMED (see api/hub.js probe status). The component
// renders defensively against the documented contract; verify before live use.

import React, { useCallback, useEffect, useRef, useState } from "react";
import { getClientContext, getSignals, getTimeline } from "../../../api/hub";
import { ErrorState, EmptyState } from "../../shared/StateMessage";
import PanelErrorBoundary from "../../shared/PanelErrorBoundary";
import NotPermitted from "./NotPermitted";
import Sentence from "./Sentence";
import { tintClasses, severityToTint } from "../../shared/tints";
import { signalTint } from "../signalTint";
import { formatWhen, telHref } from "../../shared/format";

// ── Helpers ──────────────────────────────────────────────────────────────────

// A labelled value in the standing-facts block. Renders nothing when the value
// is absent — the header shows only fields that are present (fn_client_context
// omits absent fields; we honour that by never emitting an empty row).
const FactRow = ({ label, children }) => {
  if (children == null || children === "" || (Array.isArray(children) && children.length === 0)) return null;
  return (
    <div>
      <dt className="text-[11px] uppercase tracking-wide text-gray-400">{label}</dt>
      <dd className="text-sm text-gray-900 dark:text-gray-100">{children}</dd>
    </div>
  );
};

// One signal as a tinted, contained insight surface. The `says` field is
// rendered verbatim via <Sentence> — never rebuilt from other fields.
const SignalItem = ({ signal, byMeaning }) => {
  // Default (every existing caller): fn_signals_for returns `severity`
  // (critical|warn|watch), not a tint — map it through the shared vocabulary
  // (critical/warn → warm, watch → cool); a `tint` on the row wins.
  //
  // Opt-in (`tintSignals="meaning"`): severity is an urgency and cannot say whose court
  // the ball is in, so the tint comes from signalTint(), the same function the detail
  // page's attention strip uses (an unmapped signal is neutral, never red).
  const tint = byMeaning ? signalTint(signal) : signal?.tint || severityToTint(signal?.severity);
  const t = tintClasses(tint);
  return (
    <div className={`rounded-lg border ${t.surface} px-3 py-2`}>
      <Sentence says={signal?.says || signal?.text} tint={tint} size="sm" />
    </div>
  );
};

// One timeline event — renders `says` verbatim with a compact timestamp + actor.
// `seq` is for cursor/replay pagination (fn_timeline's next_before_seq).
const TimelineEntry = ({ event }) => {
  const e = event || {};
  return (
    <li className="flex gap-3 py-2">
      <div className="shrink-0 w-16 text-xs text-gray-400 tabular-nums">
        {formatWhen(e.at)}
      </div>
      <div className="min-w-0 flex-1">
        {e.says ? (
          <Sentence says={e.says} tint="cool" size="sm" />
        ) : (
          <p className="text-sm text-gray-700 dark:text-gray-300">{e.label || e.type}</p>
        )}
        {e.actor && (
          <p className="text-xs text-gray-400">{e.actor}</p>
        )}
      </div>
    </li>
  );
};

// Elsewhere links — a link, not a copy (spec §3). Never duplicate a number that
// lives elsewhere; link to the canonical surface.
const ElsewhereLinks = ({ elsewhere }) => {
  const links = Array.isArray(elsewhere) ? elsewhere : [];
  if (links.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1">
      {links.map((l, i) => (
        <a
          key={i}
          href={l.url || l.href || "#"}
          target="_blank"
          rel="noreferrer"
          className="text-xs text-[#32B1FF] hover:underline"
        >
          {l.label || l.name || l.url}
        </a>
      ))}
    </div>
  );
};

// ── Main component ───────────────────────────────────────────────────────────

/**
 * @param {object} props
 * @param {string} props.clientId        Required — the client to load.
 * @param {string} props.clientName      Required — the trigger label (and the panel's accessible name).
 * @param {string} [props.className]     Applied to the trigger button.
 * @param {boolean} [props.open]         CONTROLLED mode: when defined, the parent owns open/closed
 *   and no built-in trigger is rendered — one instance can serve a whole list. Omit for the
 *   original self-contained trigger + panel.
 * @param {() => void} [props.onClose]   Controlled mode: called on Esc, scrim click and the close button.
 * @param {"meaning"} [props.tintSignals]  Opt-in: tint each signal by what it MEANS
 *   (signalTint(), shared with the detail page's attention strip) instead of by its
 *   severity. Omit and signals render exactly as they always have.
 * @param {string} [props.fullPageHref]  Adds an "Open full page" link to the header.
 * @param {(clientId: string) => void} [props.onOpenFullPage]  Handles that link in-app (the href
 *   stays for new-tab / copy-link); without it the link is a plain navigation.
 */
export default function ClientSlideOut({
  clientId, clientName, className = "", open: openProp, onClose, fullPageHref, onOpenFullPage, tintSignals,
}) {
  const controlled = openProp !== undefined;
  const [openState, setOpenState] = useState(false);
  const open = controlled ? !!openProp : openState;
  // The Esc listener is registered once per open and would otherwise keep calling the
  // onClose from the render it was registered in; read the latest one through a ref.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const setOpen = useCallback(
    (next) => { if (controlled) { if (!next) onCloseRef.current?.(); } else setOpenState(next); },
    [controlled],
  );
  // Each load takes a ticket; only the newest ticket may write state, so a slow
  // response for a previous client (or after the panel closed) can never overwrite
  // the current one.
  const loadTicket = useRef(0);
  const [status, setStatus] = useState("idle"); // idle | loading | ready | error
  const [context, setContext] = useState(null);
  const [signals, setSignals] = useState([]);
  const [timeline, setTimeline] = useState({ events: [] });
  // Which progressive sections failed to load — shown as "couldn't load", never as
  // "no signals" / "No recent activity".
  const [failed, setFailed] = useState({ signals: false, timeline: false });
  const panelRef = useRef(null);
  const returnFocusRef = useRef(null);

  // Fetch on open — one Promise.all, never sequential. Signals and timeline
  // are progressive enhancement: a failure in either must not break the page,
  // so each resolves to null and its section shows a retryable error. The
  // context RPC is the root — its failure fails the whole panel.
  const load = useCallback(async () => {
    if (!clientId) return;
    const ticket = ++loadTicket.current;
    setStatus("loading");
    try {
      const [ctxRes, sigRes, tlRes] = await Promise.all([
        getClientContext(clientId),
        getSignals(clientId).catch(() => null),
        getTimeline('client', clientId, { limit: 50 }).catch(() => null),
      ]);
      if (ticket !== loadTicket.current) return;
      setContext(ctxRes?.data || null);
      setFailed({ signals: sigRes == null, timeline: tlRes == null });
      setSignals(Array.isArray(sigRes?.data) ? sigRes.data : []);
      // fn_timeline returns `events: null` when a client has none; normalise so
      // the list never reads .length off null.
      setTimeline({ ...(tlRes?.data || {}), events: Array.isArray(tlRes?.data?.events) ? tlRes.data.events : [] });
      setStatus("ready");
    } catch (err) {
      if (ticket !== loadTicket.current) return;
      setStatus("error");
    }
  }, [clientId]);

  // Open → load + focus management + Esc.
  useEffect(() => {
    if (!open) return undefined;
    returnFocusRef.current = document.activeElement;
    load();
    const onKey = (e) => {
      if (e.key === "Escape") { e.stopPropagation(); setOpen(false); }
    };
    document.addEventListener("keydown", onKey, true);
    const panel = panelRef.current;
    // Prefer an explicit initial target (the close button) over whatever comes first —
    // the header may carry a link ahead of it, and Enter on open must not navigate away.
    const first = panel?.querySelector("[data-autofocus]") || panel?.querySelector("button, a, [tabindex]");
    (first || panel)?.focus();
    return () => {
      loadTicket.current += 1; // closed (or switching client): drop anything still in flight
      document.removeEventListener("keydown", onKey, true);
      const el = returnFocusRef.current;
      if (el && typeof el.focus === "function") el.focus({ preventScroll: true });
    };
  }, [open, load]);

  if (!clientName && !open) return null;
  if (controlled && !open) return null;

  const ctx = context || {};
  const client = ctx.client || {};
  const location = [client.city, client.state].filter(Boolean).join(", ") || null;
  const contacts = Array.isArray(client.contacts) ? client.contacts : [];

  return (
    <>
      {!controlled && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className={`text-sm text-[#32B1FF] hover:underline ${className}`}
          data-testid="client-slideout-trigger"
        >
          {clientName}
        </button>
      )}

      {open && (
        <div className="fixed inset-0 z-50 flex" role="dialog" aria-label={`${clientName || "Client"} details`}>
          <div className="flex-1 bg-black/20 backdrop-blur-sm" onClick={() => setOpen(false)} aria-hidden="true" />
          <aside
            ref={panelRef}
            tabIndex={-1}
            className="w-full md:w-[480px] md:max-w-[calc(100vw-60px)] h-full bg-white/90 dark:bg-gray-900/90 backdrop-blur-md shadow-2xl border-l border-gray-200/50 dark:border-gray-800/50 flex flex-col focus:outline-none"
            data-testid="client-slideout-panel"
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200/50 dark:border-gray-800/50">
              <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100 truncate">
                {client.name || clientName}
              </h2>
              {fullPageHref && (
                <a
                  href={fullPageHref}
                  onClick={(e) => {
                    if (!onOpenFullPage || e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
                    e.preventDefault();
                    onOpenFullPage(clientId);
                  }}
                  className="ml-3 mr-2 shrink-0 text-sm font-medium text-[#32B1FF] hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#32B1FF] rounded"
                  data-testid="client-slideout-full-page"
                >
                  Open full page
                </a>
              )}
              <button
                type="button"
                onClick={() => setOpen(false)}
                data-autofocus
                className="shrink-0 p-1.5 -mr-1.5 rounded-md text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#32B1FF]"
                aria-label="Close"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            {/* Body — the event stream is the tallest thing and scrolls.
                Each region is wrapped in PanelErrorBoundary so one degrade
                never blanks the slide-out. */}
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
              {status === "loading" && (
                <div className="space-y-3" role="status" aria-label="Loading">
                  <div className="h-6 rounded-lg bg-gray-100 dark:bg-gray-800 animate-pulse w-2/3" />
                  <div className="h-4 rounded-lg bg-gray-100 dark:bg-gray-800 animate-pulse w-1/2" />
                  <div className="h-4 rounded-lg bg-gray-100 dark:bg-gray-800 animate-pulse w-3/4" />
                  <span className="sr-only">Loading…</span>
                </div>
              )}

              {status === "error" && (
                <ErrorState variant="inset" message="Couldn't load this client's details." onRetry={load} />
              )}

              {status === "ready" && (
                <>
                  {/* Standing facts — quiet, dense, permanent (top). */}
                  <PanelErrorBoundary label="Standing facts">
                    <section aria-label="Standing facts">
                      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
                        <FactRow label="Location">{location}</FactRow>
                        <FactRow label="Website">
                          {client.website ? (
                            <a href={client.website} target="_blank" rel="noreferrer" className="text-[#32B1FF] hover:underline break-all">
                              {client.website}
                            </a>
                          ) : null}
                        </FactRow>
                        <FactRow label="Services">
                          {Array.isArray(client.active_services) && client.active_services.length > 0
                            ? client.active_services.join(", ") : null}
                        </FactRow>
                        {contacts.map((c, i) => (
                          <FactRow key={i} label={c.role || c.label || "Contact"}>
                            {c.phone ? (
                              <a href={telHref(c.phone)} className="text-[#32B1FF] hover:underline">{c.phone}</a>
                            ) : c.email ? (
                              <a href={`mailto:${c.email}`} className="text-[#32B1FF] hover:underline">{c.email}</a>
                            ) : c.name}
                          </FactRow>
                        ))}
                      </dl>
                    </section>
                  </PanelErrorBoundary>

                  {/* Signals — tinted, contained insight surfaces. */}
                  {failed.signals && (
                    <ErrorState variant="inset" message="Couldn't load signals for this client." onRetry={load} />
                  )}
                  {signals.length > 0 && (
                    <PanelErrorBoundary label="Signals">
                      <section aria-label="Signals" className="space-y-2">
                        {signals.map((sig, i) => (
                          <SignalItem key={sig.id || i} signal={sig} byMeaning={tintSignals === "meaning"} />
                        ))}
                      </section>
                    </PanelErrorBoundary>
                  )}

                  {/* Attention — the "what needs you" sentence from context. */}
                  {ctx.attention && (
                    <PanelErrorBoundary label="Attention">
                      <section aria-label="Attention">
                        <Sentence
                          says={typeof ctx.attention === "string" ? ctx.attention : ctx.attention.says}
                          tint={ctx.attention.tint || "warm"}
                          size="md"
                        />
                      </section>
                    </PanelErrorBoundary>
                  )}

                  {/* Billing — gated: null = "you may not see this". Never zero,
                      never empty. The null IS the permission signal. */}
                  <PanelErrorBoundary label="Billing">
                    {ctx.billing == null ? (
                      <NotPermitted title="Billing" message="You don't have access to billing." />
                    ) : (
                      <section aria-label="Billing">
                        <Sentence
                          says={typeof ctx.billing === "string" ? ctx.billing : ctx.billing.says}
                          tint={ctx.billing.tint || "cool"}
                          size="md"
                        />
                      </section>
                    )}
                  </PanelErrorBoundary>

                  {/* Effort — gated, same rule as billing. */}
                  <PanelErrorBoundary label="Effort">
                    {ctx.effort == null ? (
                      <NotPermitted title="Effort" message="You don't have access to effort." />
                    ) : (
                      <section aria-label="Effort">
                        <Sentence
                          says={typeof ctx.effort === "string" ? ctx.effort : ctx.effort.says}
                          tint={ctx.effort.tint || "cool"}
                          size="md"
                        />
                      </section>
                    )}
                  </PanelErrorBoundary>

                  {/* Event stream — the tallest thing, scrolls. */}
                  <PanelErrorBoundary label="Timeline">
                    <section aria-label="Event stream">
                      <h3 className="text-[11px] uppercase tracking-wide text-gray-400 mb-2">Activity</h3>
                      {failed.timeline ? (
                        <ErrorState variant="inset" message="Couldn't load activity for this client." onRetry={load} />
                      ) : timeline.events.length === 0 ? (
                        <EmptyState variant="inset" message="No recent activity." padding="py-6" />
                      ) : (
                        <ul className="divide-y divide-gray-100 dark:divide-gray-800">
                          {timeline.events.map((ev, i) => (
                            <TimelineEntry key={ev.seq || i} event={ev} />
                          ))}
                        </ul>
                      )}
                    </section>
                  </PanelErrorBoundary>

                  {/* Elsewhere — links, not copies (spec §3). */}
                  {ctx.elsewhere && (
                    <PanelErrorBoundary label="Elsewhere">
                      <ElsewhereLinks elsewhere={ctx.elsewhere} />
                    </PanelErrorBoundary>
                  )}
                </>
              )}
            </div>
          </aside>
        </div>
      )}
    </>
  );
}

